import { COINS_DISABLED } from '../config/coinsFlag';
/**
 * BoostSelectionModal - Psychology-Driven Monetization Funnel
 * 
 * UI/UX redesign — zero changes to business logic, wallet, pricing,
 * API calls, boost activation, disabled states, or translations.
 *
 * Funnel structure:
 * 1. Title + trust-building subtitle
 * 2. Wallet balance chip
 * 3. Before / After boost explanation
 * 4. Recommended option visually dominant
 * 5. Options ordered lighter → stronger
 * 6. Post-selection outcome clarity
 * 7. Low-balance / recharge section
 * 8. Primary CTA + secondary close
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Zap, X, Loader2, Clock, TrendingUp, 
  AlertTriangle, Sparkles, RefreshCw, Crown, Check, Home,
  AlertCircle, Eye, ArrowUp, Star, Wallet, Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  BOOST_OPTIONS, 
  getBoostName, 
  getBoostDescription, 
  formatDuration,
  applyRegionalPricing
} from '../config/boostOptions';
import { formatCoins } from '../lib/coinFormat';
import { canBoostAd as canBoostAdHelper } from './AvailabilityBadge';
import { BoostImpactPreview } from './WalletMonetization';
import { getLabel } from '../config/monetizationConfig';
import { trackBoostAttemptWithoutCoins } from '../utils/monetizationAnalytics';
import { getApiBase } from '../api/httpClient';
import { safeReadResponse, pickErrorMessage } from '../api/safeFetch';
import './BoostSelectionModal.css';

// iter303 — generate-once idempotency key. Survives re-renders for the
// lifetime of this modal mount but is regenerated when the user closes
// + reopens it. Backend uses it to dedupe rapid double-taps.
function newIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* fall through */ }
  return `boost_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

// ── Icon mapping for boost types ──
const BOOST_ICONS = {
  highlight: Sparkles,
  urgent: AlertTriangle,
  refresh: RefreshCw,
  top: Crown,
  home: Home,
};

// ── The recommended boost option id ──
const RECOMMENDED_ID = 'auto_refresh_24h';

// ── Outcome-first benefit copy (not percentages) ──
const OUTCOME_COPY = {
  highlight_24h: {
    en: 'Stand out with a highlighted listing — more eyes on your ad',
    ar: 'تميّز بإعلان مُبرز — مشاهدات أكثر لإعلانك',
  },
  urgent_7d: {
    en: 'Get faster responses with an urgent badge for 7 days',
    ar: 'فرصة أكبر للتواصل الأسرع مع شارة عاجل لمدة 7 أيام',
  },
  auto_refresh_24h: {
    en: 'Stay on top — your ad refreshes automatically for more views',
    ar: 'ابقَ في المقدمة — تحديث تلقائي لمشاهدات أكثر خلال 24 ساعة',
  },
  top_category_24h: {
    en: 'Higher placement in your category — be the first ad buyers see',
    ar: 'ظهور أعلى داخل القسم — كن أول ما يراه المشترون',
  },
  homepage_featured_24h: {
    en: 'Featured on the homepage — maximum visibility across the app',
    ar: 'مُميز في الرئيسية — أقصى ظهور عبر التطبيق',
  },
};

// ── Momentum cues (real value signals, no fake claims) ──
const MOMENTUM_TAGS = {
  urgent_7d: { en: 'Fast results', ar: 'نتائج سريعة', cls: 'fast' },
  auto_refresh_24h: { en: 'Best value', ar: 'أفضل قيمة', cls: 'best-value' },
  top_category_24h: { en: 'Popular', ar: 'الأكثر استخداماً', cls: 'popular' },
  homepage_featured_24h: { en: 'Popular', ar: 'الأكثر استخداماً', cls: 'popular' },
};

export default function BoostSelectionModal({ 
  open, 
  onClose, 
  ad, 
  userCoins = 0, 
  onBoostSuccess 
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const lang = i18n.language || 'en';
  const isAr = lang?.toLowerCase?.().startsWith('ar');
  
  const [selectedBoost, setSelectedBoost] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [regionalOptions, setRegionalOptions] = useState(BOOST_OPTIONS);

  // Fetch regional boost pricing on open
  useEffect(() => {
    if (!open) return;
    const fetchPricing = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/monetization/packages`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.boostPricing) {
          setRegionalOptions(applyRegionalPricing(data.boostPricing));
        }
      } catch (e) {
        console.error('Failed to fetch regional boost pricing:', e);
      }
    };
    fetchPricing();
  }, [open]);
  
  // Sort options: recommended first, then by price ascending
  const sortedOptions = useMemo(() => {
    const sorted = [...regionalOptions].sort((a, b) => {
      if (a.id === RECOMMENDED_ID) return -1;
      if (b.id === RECOMMENDED_ID) return 1;
      return a.price - b.price;
    });
    return sorted;
  }, [regionalOptions]);

  // iter303 — Idempotency key MUST be declared BEFORE any early return
  // so the hook order stays stable across renders (React hooks rule).
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  if (COINS_DISABLED) return null;
  if (!open) return null;
  // iter347 — Guard: never render the boost picker without a specific ad.
  // Callers must select an ad first (either from My Ads, Ad Details, or
  // the new /wallet/boost/choose-ad picker) before this modal opens.
  if (!ad || !(ad.ad_id || ad._id)) return null;

  // ── Labels (reusing existing translation keys) ──
  const labels = {
    title: t('bam.boostYourAd'),
    subtitle: isAr ? 'اختر الباقة المناسبة لزيادة ظهور إعلانك' : 'Choose the right package to increase your ad visibility',
    adTitle: ad?.title || '',
    balance: t('studpage.yourBalance'),
    coins: t('coins'),
    cancel: t('bam.cancel'),
    purchase: isAr ? 'عزّز الآن' : 'Boost now',
    purchasing: t('bsm.purchasing'),
    insufficientCoins: t('boosmoda.insufficientBalance'),
    selectBoost: isAr ? 'اختر نوع التعزيز' : 'Choose boost type',
    success: t('boosmoda.adBoostedSuccessfully'),
    error: t('boosmoda.boostFailedPleaseTryAgain'),
    notPublished: t('boosmoda.adMustBePublishedFirst'),
    // iter342 — Replaced "Recharge balance" (paid) with earned-only
    // "Earn more coins" CTA that deep-links into the Wallet's
    // Earn-More-Coins section. No third-party payment entry points
    // are exposed in the app anymore.
    earnMoreCoins: isAr ? 'اكسب المزيد من العملات' : 'Earn more coins',
  };

  // ── Ad eligibility ──
  // iter269: route through the canonical `canBoostAd` helper so this modal,
  // BoostModal, Wallet, and Account all agree on what "boostable" means.
  // The previous inline `adStatus === 'published'` check rejected every
  // real ad because actual published ads carry `status === 'active'`.
  const canBoostAd = canBoostAdHelper(ad);
  
  const handleSelectBoost = (boost) => {
    if (userCoins >= boost.price) {
      setSelectedBoost(boost);
    }
  };

  // ── Purchase handler (iter303 — safe response read + idempotency) ──
  // The Boost flow used to call response.json() and on some
  // network paths (slow connections + double-taps, or Sentry-style
  // wrappers reading the body for breadcrumbs) the same Response was
  // consumed twice — producing the dreaded
  // "Failed to execute 'json' on 'Response': body stream already read"
  // toast. We now read the body EXACTLY ONCE via `safeReadResponse`
  // and pass an idempotency key the backend uses to dedupe.

  const handlePurchase = async () => {
    if (!selectedBoost || purchasing || !canBoostAd) return;

    if (userCoins < selectedBoost.price) {
      toast.error(labels.insufficientCoins);
      return;
    }

    setPurchasing(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBase()}/api/boost/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          ad_id: ad.ad_id,
          boost_code: selectedBoost.code,
          idempotency_key: idempotencyKey,
        }),
      });

      // ✅ One read — never .json() again on this Response.
      const parsed = await safeReadResponse(response);

      if (!parsed.ok) {
        // Surface a friendly localized message; never raw browser errors.
        const fallback = isRTL
          ? 'تعذر إكمال تعزيز الإعلان. لم يتم خصم أي عملات.'
          : 'Boost could not be completed. Your coins were not deducted.';
        throw new Error(pickErrorMessage(parsed, fallback));
      }

      toast.success(labels.success);
      // iter342 — Sync canonical balance globally the moment the boost
      // succeeds so Header/Wallet/Account/Boost modal all reflect the
      // deducted amount from a single source of truth (AuthContext).
      const respData = parsed.data || {};
      const newBalance = typeof respData.new_balance === 'number' ? respData.new_balance : null;
      if (newBalance !== null) {
        try { updateUser({ coins: newBalance }); } catch (_) { /* no-op */ }
      }
      onBoostSuccess?.({
        ...respData,
        boost_type: selectedBoost.id,
        boost_code: selectedBoost.code,
      });
      onClose();

    } catch (e) {
      console.error('[BoostSelectionModal] Purchase failed:', e);
      const friendly = isRTL
        ? 'تعذر إكمال تعزيز الإعلان. لم يتم خصم أي عملات.'
        : 'Boost could not be completed. Your coins were not deducted.';
      // Filter out raw fetch/stream errors so users never see them.
      const raw = e?.message || '';
      const isLowLevel = /body stream already read|Failed to fetch|NetworkError/i.test(raw);
      toast.error(isLowLevel ? friendly : (raw || friendly));
    } finally {
      setPurchasing(false);
    }
  };

  // Does any option exceed the user balance?
  const anyUnaffordable = BOOST_OPTIONS.some(b => b.price > userCoins);
  const allUnaffordable = BOOST_OPTIONS.every(b => b.price > userCoins);

  return (
    <div 
      className="boost-premium-overlay"
      onClick={() => !purchasing && onClose()}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="boost-selection-modal"
    >
      <div 
        className="boost-premium-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ════════════════════════════════════════
            1. HEADER — Title + Subtitle + Close
        ════════════════════════════════════════ */}
        <div className="boost-premium-header">
          <button 
            className="boost-close-btn"
            onClick={() => !purchasing && onClose()}
            disabled={purchasing}
            aria-label={labels.cancel}
            data-testid="boost-selection-close"
          >
            <X size={16} />
          </button>
          
          <div className="boost-header-content">
            <div className="boost-header-icon">
              <Zap size={26} />
            </div>
            <h2 className="boost-header-title">{labels.title}</h2>
            <p className="boost-header-subtitle">{labels.subtitle}</p>
            
            {/* 2. WALLET BALANCE CHIP */}
            <div className="boost-balance-pill" data-testid="boost-balance-pill">
              <Wallet size={14} />
              <span>{labels.balance}</span>
              <span className="bal-amount">{formatCoins(userCoins, lang)}</span>
            </div>
          </div>
        </div>

        {/* Ad status warning */}
        {!canBoostAd && (
          <div className="boost-status-warn" data-testid="boost-status-warning">
            <AlertTriangle size={16} />
            {labels.notPublished}
          </div>
        )}

        {/* ════════════════════════════════════════
            SCROLLABLE BODY
        ════════════════════════════════════════ */}
        <div className="boost-premium-body">

          {/* 3. BEFORE / AFTER BLOCK */}
          <div className="boost-before-after" data-testid="boost-before-after">
            <div className="boost-ba-card boost-ba-before">
              <Eye size={20} style={{ color: '#9CA3AF' }} />
              <div className="ba-label">{isAr ? 'قبل التعزيز' : 'Before'}</div>
              <div className="ba-text">
                {isAr ? 'يظهر إعلانك بشكل عادي' : 'Your ad appears normally'}
              </div>
            </div>
            <div className="boost-ba-card boost-ba-after">
              <TrendingUp size={20} style={{ color: '#EA580C' }} />
              <div className="ba-label">{isAr ? 'بعد التعزيز' : 'After'}</div>
              <div className="ba-text">
                {isAr 
                  ? 'ظهور أفضل\nمشاهدات أكثر\nفرص تواصل أسرع'
                  : 'Better visibility\nMore views\nFaster responses'}
              </div>
              {/* Tiny boost badge previews */}
              <div className="boost-preview-badges" data-testid="boost-preview-badges">
                <span className="boost-preview-badge featured">
                  <Star size={8} />
                  {isAr ? 'مميز' : 'Featured'}
                </span>
                <span className="boost-preview-badge top">
                  <ArrowUp size={8} />
                  {isAr ? 'أعلى القسم' : 'Top'}
                </span>
                <span className="boost-preview-badge boosted">
                  <Zap size={8} />
                  {isAr ? 'معزّز' : 'Boosted'}
                </span>
              </div>
            </div>
          </div>

          {/* 4–5. BOOST OPTION CARDS */}
          <div className="boost-section-label">
            {isAr ? 'اختر الباقة' : 'Choose a package'}
          </div>

          <div className="boost-options-list" data-testid="boost-options-list">
            {sortedOptions.map((boost) => {
              const Icon = BOOST_ICONS[boost.icon] || Zap;
              const canAfford = userCoins >= boost.price;
              const isSelected = selectedBoost?.id === boost.id;
              const isDisabled = !canAfford || !canBoostAd;
              const isRecommended = boost.id === RECOMMENDED_ID;
              const outcome = OUTCOME_COPY[boost.id];
              const momentum = MOMENTUM_TAGS[boost.id];
              
              const classes = [
                'boost-option-card',
                isSelected && 'is-selected',
                isDisabled && 'is-disabled',
                isRecommended && 'is-recommended',
              ].filter(Boolean).join(' ');
              
              return (
                <button
                  key={boost.id}
                  onClick={() => !isDisabled && handleSelectBoost(boost)}
                  disabled={isDisabled}
                  className={classes}
                  style={{ '--boost-color': boost.color }}
                  data-testid={`boost-option-${boost.id}`}
                >
                  {/* Recommended badge */}
                  {isRecommended && (
                    <span className="boost-rec-badge" data-testid="boost-recommended-badge">
                      {isAr ? 'الأفضل' : 'Recommended'}
                    </span>
                  )}

                  {/* Icon */}
                  <div 
                    className="boost-opt-icon"
                    style={{ background: isDisabled ? undefined : `${boost.color}15` }}
                  >
                    <Icon size={20} style={{ color: isDisabled ? '#9CA3AF' : boost.color }} />
                  </div>
                  
                  {/* Content */}
                  <div className="boost-opt-content">
                    <div className="boost-opt-title-row">
                      <span className="boost-opt-name">
                        {getBoostName(boost, lang)}
                      </span>
                      <span 
                        className="boost-opt-price"
                        style={{ color: isDisabled ? '#9CA3AF' : boost.color }}
                      >
                        {formatCoins(boost.price, lang)}
                      </span>
                    </div>
                    
                    {/* Outcome-first benefit line */}
                    <div className="boost-opt-benefit">
                      {outcome ? (isAr ? outcome.ar : outcome.en) : getBoostDescription(boost, lang)}
                    </div>
                    
                    {/* Meta: duration + momentum tag */}
                    <div className="boost-opt-meta">
                      <span className="boost-opt-duration">
                        <Clock size={11} />
                        {formatDuration(boost.duration_hours, lang)}
                      </span>
                      
                      {momentum && (
                        <span className={`boost-tag ${momentum.cls}`}>
                          {isAr ? momentum.ar : momentum.en}
                        </span>
                      )}
                    </div>
                    
                    {/* Insufficient balance per-card */}
                    {!canAfford && (
                      <div className="boost-opt-insufficient">
                        <AlertCircle size={11} />
                        {labels.insufficientCoins}
                      </div>
                    )}
                    
                    {/* Boost Impact Preview (existing component) */}
                    {canAfford && !isDisabled && (
                      <BoostImpactPreview
                        lang={lang}
                        boostType={boost.id}
                        baseViews7d={ad?.views || 50}
                        baseCtr={0.018}
                      />
                    )}
                  </div>
                  
                  {/* Selected checkmark */}
                  {isSelected && (
                    <div 
                      className="boost-opt-check"
                      style={{ background: boost.color }}
                      data-testid="boost-option-selected-check"
                    >
                      <Check size={14} color="white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* 6. POST-SELECTION OUTCOME */}
          {selectedBoost && (
            <div className="boost-post-select" data-testid="boost-post-select-info">
              <div className="ps-icon">
                <Check size={16} />
              </div>
              <div className="ps-text">
                <strong>{getBoostName(selectedBoost, lang)}</strong>
                <br />
                {isAr 
                  ? `سيتم تفعيل التعزيز فور التأكيد — المدة: ${formatDuration(selectedBoost.duration_hours, lang)}`
                  : `This boost activates immediately after confirmation — Duration: ${formatDuration(selectedBoost.duration_hours, lang)}`
                }
              </div>
            </div>
          )}

          {/* 7. LOW-BALANCE — EARNED-COINS-ONLY CTA
              iter342 — All paid entry points (Recharge, Bankak/Visa,
              Pro/Save-more-with-Pro subscription upsell) have been
              removed. The only path forward when the balance is
              insufficient is to earn more coins via wallet tasks. */}
          {anyUnaffordable && (
            <div className="boost-low-balance" data-testid="boost-low-balance-section">
              {/* Exact shortage for selected boost */}
              {selectedBoost && userCoins < selectedBoost.price && (
                <div className="lb-shortage" data-testid="boost-shortage-msg">
                  <AlertCircle size={14} />
                  {isAr
                    ? `تحتاج ${selectedBoost.price - userCoins} عملة إضافية فقط`
                    : `You only need ${selectedBoost.price - userCoins} more coins`
                  }
                </div>
              )}
              {!selectedBoost && (
                <div className="lb-msg">
                  <AlertCircle size={16} />
                  {getLabel('need_more_coins', lang)}
                </div>
              )}
              <button
                className="boost-earn-more-btn"
                onClick={() => {
                  const shortage = selectedBoost ? selectedBoost.price - userCoins : 5;
                  try { trackBoostAttemptWithoutCoins(selectedBoost?.id, shortage); } catch (_) { /* no-op */ }
                  onClose?.();
                  // Deep-link into Wallet's "Earn More Coins" section.
                  navigate('/wallet?scroll=earn');
                }}
                data-testid="boost-earn-more-btn"
              >
                <Coins size={14} />
                {labels.earnMoreCoins}
              </button>
              <p
                className="text-[11px] text-slate-500 mt-2 leading-snug text-center"
                data-testid="boost-earn-more-hint"
              >
                {isAr
                  ? 'أكمل المهام في المحفظة لجمع العملات ثم عزّز إعلانك.'
                  : 'Complete tasks in your wallet to collect coins and boost your ad.'}
              </p>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════
            8. FOOTER — CTA + Cancel
        ════════════════════════════════════════ */}
        <div className="boost-premium-footer">
          <div className="boost-footer-grid">
            <button
              className="boost-cancel-btn"
              onClick={() => !purchasing && onClose()}
              disabled={purchasing}
              data-testid="boost-selection-cancel"
            >
              {labels.cancel}
            </button>
            
            <button
              className={`boost-purchase-btn ${selectedBoost && canBoostAd ? 'has-selection' : ''}`}
              onClick={handlePurchase}
              disabled={!selectedBoost || purchasing || !canBoostAd}
              style={selectedBoost && canBoostAd ? {
                background: `linear-gradient(135deg, ${selectedBoost.color} 0%, ${selectedBoost.color}DD 100%)`,
              } : undefined}
              data-testid="boost-selection-confirm"
            >
              {purchasing ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {labels.purchasing}
                </>
              ) : selectedBoost ? (
                <>
                  <Zap size={18} />
                  {labels.purchase} ({formatCoins(selectedBoost.price, lang)})
                </>
              ) : (
                labels.selectBoost
              )}
            </button>
          </div>
        </div>
      </div>

      {/* iter342 — PurchaseCoinsModal mount removed. All paid purchase
          entry points (recharge/top-up/Bankak/Visa/Pro subscription)
          are hidden from the app. Balance top-ups happen only via
          earned tasks in the Wallet page. */}
    </div>
  );
}

export { BOOST_OPTIONS };

